import { styled } from "@mui/material";
import { SelectFieldOption } from "controls/global/select-field/SelectInput";
import StewartMultiSelectField from "controls/global/stewart-multi-select/StewartMultiSelectField";
import { groupBy, sortBy } from "lodash";
import { useSignatureDefaultDialogProvider } from "pages/signatures/providers/signature-defaults/SignatureDefaultDialogProvider";
import React, { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { margin } from "theme/defaultStyle";
import { useAgencyLocationByImage, useDefaultsState  } from "utils/context/SignatureDefaultsContext";

const FormField = styled("div")({
  marginTop: margin.xlarge1,
});

const FIELD_NAME = "location";

const MultiSelectAgencyLocationField = () => {
  const [{ agencyLocationsByImage }] = useAgencyLocationByImage();
  const [{ defaults }] = useDefaultsState();
  const [agencyField, stateField, locationField, signatureImageField, levelField] =
    useWatch({
      name: ["agency", "state", "location", "signatureImage", "level"],
    });

  const { mode, setUserInteracted } = useSignatureDefaultDialogProvider();

  const [availableLocations, setAvailableLocations] = useState<
    SelectFieldOption[]
  >([]);
  const [excludeValue, setExcludeValue] = useState<string[] | undefined>(
    undefined
  );
  const userSelectedLocationsRef = React.useRef<SelectFieldOption[]>([]);
  const hasUserInteractedRef = React.useRef(false);
  const prevAgencyRef = React.useRef<string | undefined>(undefined);
  const prevStateValueRef = React.useRef<string | undefined>(undefined);
  const prevAgencyLocationsKeyRef = React.useRef<string>("");
  const prevLevelRef = React.useRef<string | undefined>(undefined);
  const { setValue, getValues } = useFormContext();

  const agencyLocationsKey = useMemo(() => {
    return agencyLocationsByImage.map(l => l.locationUniqueID).sort().join("|");
  }, [agencyLocationsByImage]);

  const handleLocationChange = (
    value: SelectFieldOption[],
    selectedOption: SelectFieldOption | undefined
  ) => {
    hasUserInteractedRef.current = true;
    setUserInteracted();

    if (selectedOption) {
      const isOptionInValue = value.some((v) => v.value === selectedOption.value);
      
      if (isOptionInValue) {
        const alreadyExists = userSelectedLocationsRef.current.some(
          (loc) => loc.value === selectedOption.value
        );
        if (!alreadyExists) {
          userSelectedLocationsRef.current.push(selectedOption);
        }
      } else {
        userSelectedLocationsRef.current = userSelectedLocationsRef.current.filter(
          (loc) => loc.value !== selectedOption.value
        );
      }
    }
    
    const cleanedValue = value.filter((v) => v.text && v.value);

    const allOption = availableLocations.find((loc) => loc.value === "All");
    const nonAllOptions = availableLocations.filter(
      (loc) => loc.value !== "All"
    );
    const isAllSelected = selectedOption?.value === "All";
    const hasAllInValue =
      Array.isArray(value) && value.some((v: any) => v.value === "All");

    if (allOption) {
      if (hasAllInValue && isAllSelected) {
        // User selected "All"
        setValue(FIELD_NAME, availableLocations);
      } else if (!hasAllInValue && isAllSelected) {
        // User deselected "All"
        setValue(FIELD_NAME, []);
      } else if (
        hasAllInValue &&
        !isAllSelected &&
        cleanedValue.length < availableLocations.length
      ) {
        // User deselected a specific location while "All" was selected - remove "All"
        const newValue = cleanedValue.filter((v) => v.value !== "All");
        setValue(FIELD_NAME, newValue);
      } else if (
        !isAllSelected &&
        cleanedValue.length === nonAllOptions.length &&
        cleanedValue.length > 0
      ) {
        // User manually selected all non-All options - add "All" automatically
        setValue(FIELD_NAME, availableLocations);
      }
    }
  };

  const preselectOptions = useMemo(() => {
    if (mode !== "ADD") return [];

    const allAgencies = agencyField === "All";
    const signatureImageID = Number(signatureImageField?.value);
    const levelTypeCode = levelField?.value;

    const matchingDefaults = defaults.filter(
      (d) =>
       {
        return  d.signatureImageID === signatureImageID &&
        d.signatureLevelTypeCode === levelTypeCode &&
        (allAgencies ? d.agencyID === "All" : d.agencyID === agencyField) &&
        (d.stateCode === stateField?.value)
       }
    );

    const hasAll = matchingDefaults.some((d) => d.locationUniqueIDs === "All");
    if (hasAll) {
      return [{ text: "All Agency Locations", value: "All" }];
    }

    return matchingDefaults.map((d) => ({
      text: d.locationDisplayName || (d.locationUniqueIDs === "All" ? "All Agency Locations" : ""),
      value: d.locationUniqueIDs,
    }));
  }, [mode, defaults, agencyField, stateField, signatureImageField, levelField]);

  useEffect(() => {
    const levelValue = levelField?.value;
    const levelChanged = prevLevelRef.current !== undefined && prevLevelRef.current !== levelValue;
    
    if (levelChanged && userSelectedLocationsRef.current.length > 0) {
      
      const mergedLocations = [...userSelectedLocationsRef.current];
      preselectOptions.forEach(preselect => {
        if (!mergedLocations.some(loc => loc.value === preselect.value)) {
          mergedLocations.push(preselect);
        }
      });

      mergedLocations.sort((a, b) => a.text.localeCompare(b.text));

      const hasAll = mergedLocations.some((opt) => opt.value === "All");
      
      if (mergedLocations.length > 0) {
        setValue(FIELD_NAME, hasAll ? availableLocations : mergedLocations);
      }
    } else if(levelChanged && preselectOptions.length === 0) {
      setValue(FIELD_NAME, []);
    } else if(levelChanged && preselectOptions.length > 0 && userSelectedLocationsRef.current.length === 0) {
      const hasAll = preselectOptions.some((opt) => opt.value === "All");
      setValue(FIELD_NAME, hasAll ? availableLocations : preselectOptions);
    }
    
    prevLevelRef.current = levelValue;
    
    if (hasUserInteractedRef.current) return;
    
    if (mode === "ADD" && preselectOptions.length > 0) {
      const currentValue = getValues(FIELD_NAME);
      
      if (!currentValue || (currentValue && currentValue?.value === '') || (Array.isArray(currentValue) && currentValue.length === 0)) {
        const hasAll = preselectOptions?.some((opt) => opt.value === "All");
        setValue(FIELD_NAME, hasAll ? availableLocations : preselectOptions);
      }
    }
  }, [mode, preselectOptions, getValues, setValue, availableLocations, levelField]);

  useEffect(() => {
    const stateValue = stateField?.value;
    
    const agencyChanged = prevAgencyRef.current !== undefined && prevAgencyRef.current !== agencyField;
    const stateChanged = prevStateValueRef.current !== undefined && prevStateValueRef.current !== stateValue;
    const locationsChanged = prevAgencyLocationsKeyRef.current !== "" && 
      prevAgencyLocationsKeyRef.current !== agencyLocationsKey;
    
    if (agencyChanged || stateChanged || locationsChanged) {
      hasUserInteractedRef.current = false;
      userSelectedLocationsRef.current = [];
    }
    
    prevAgencyRef.current = agencyField;
    prevStateValueRef.current = stateValue;
    prevAgencyLocationsKeyRef.current = agencyLocationsKey;
    
    const agency = agencyField;
    const state = String(stateValue);
    const allAgencies = agency === "All";
    const allStates = state === "All";

    const filteredLocations = agencyLocationsByImage.filter(
      (l) =>
        (allAgencies || l.agencyID === agency) &&
        (allStates || l.stateCode === state)
    );

    const groupedLocations = groupBy(
      filteredLocations.map((g) => ({
        locationDisplayName: g.locationDisplayName,
        locationUniqueID: g.locationUniqueID,
      })),
      (a) => a.locationDisplayName
    );

    let locations: SelectFieldOption[] = sortBy(
      Object.values(groupedLocations).map((g) => ({
        value: g.map((i) => i.locationUniqueID).sort().join("|"),
        text: g[0].locationDisplayName,
      })),
      (i) => i.text
    );

    if (locations.length > 1) {
      locations = [
        { text: "All Agency Locations", value: "All" },
        ...locations,
      ];
    }

    setAvailableLocations(locations);
  }, [agencyField, stateField?.value, agencyLocationsKey]);

  React.useEffect(() => {
    if (Array.isArray(locationField) && locationField.length > 0) {
      const hasAll = locationField.some((v: any) => v.value === "All");
      if (hasAll) {
        // When "All" is selected, exclude all individual location values from display
        const nonAllValues = availableLocations
          .filter((loc) => loc.value !== "All")
          .map((loc) => loc.value);
        setExcludeValue(nonAllValues);
      } else {
        setExcludeValue(undefined);
      }
    } else {
      setExcludeValue(undefined);
    }
  }, [locationField, availableLocations]);

  return (
    <FormField>
      <StewartMultiSelectField
        name={FIELD_NAME}
        label="Agency Location"
        options={availableLocations}
        onChange={(e, value, selectedOption) =>
          handleLocationChange(value, selectedOption)
        }
        disabled={availableLocations.length < 1}
        excludeFromDisplayValue={excludeValue}
        disableAutoSelectSingle={true}
        shouldSetDefaultSelectedValue={true}
      />
    </FormField>
  );
};

export default MultiSelectAgencyLocationField;
